import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuSearch, LuCircleCheck, LuX, LuCheck, LuArrowLeft } from 'react-icons/lu';
import TraderLayout from '../../components/TraderLayout/TraderLayout';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import MarketplaceItemDrawer from '../../components/MarketplaceItemDrawer/MarketplaceItemDrawer';
import PlaceOrderDrawer from '../../components/PlaceOrderDrawer/PlaceOrderDrawer';
import { fetchApprovedBatches } from '../../lib/directus';
import styles from './BuyerMarketplace.module.css';
import CheckmarkBadgeIcon from '../../icons/CheckmarkBadgeIcon';

function mapBatch(b) {
  const certs = (b.certificates || []).map((c) =>
    typeof c === 'string' ? c : (c.filename_download || c.title || `Certificate ${c.id}`)
  );
  const qty       = parseFloat(b.quantity) || 0;
  const unitPrice = parseFloat(b.unit_price) || 0;
  const total     = qty * unitPrice;
  return {
    _id: b.id,
    batchId: '#BCH-' + String(b.id).slice(-8).toUpperCase(),
    commodity: b.commodity || '-',
    grade: b.grade || '-',
    quantity: qty ? `${qty}${b.unit_of_measure ? ' ' + b.unit_of_measure : ' MT'}` : '-',
    rawQuantity: qty,
    location: b.location || '-',
    seller: b.seller?.company_name || '-',
    sellerUserId: b.seller?.contact || null,
    unitPrice,
    price: unitPrice ? `R${unitPrice.toLocaleString()}` : '-',
    totalAmount: total ? `R${total.toLocaleString()}` : '-',
    moisture: b.moisture_content != null ? `${b.moisture_content}%` : '-',
    foreign: b.foreign_matter != null ? `${b.foreign_matter}%` : '-',
    broken: b.damaged != null ? `${b.damaged}%` : '-',
    sellerRating: 0,
    sellerTrades: 0,
    certs,
  };
}

function SuccessModal({ onClose }) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}><LuX size={18} /></button>
        <div className={styles.successIcon}><LuCheck size={32} color="white" /></div>
        <p className={styles.successMsg}>Order submitted successfully</p>
      </div>
    </div>
  );
}

export default function BuyerMarketplace() {
  const navigate = useNavigate();
  const [listings, setListings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [category, setCategory]         = useState('Categories');
  const [grade, setGrade]               = useState('Grade');
  const [selectedItem, setSelectedItem] = useState(null);
  const [orderingItem, setOrderingItem] = useState(null);
  const [showSuccess, setShowSuccess]   = useState(false);

  useEffect(() => {
    fetchApprovedBatches()
      .then((data) => setListings(data.map(mapBatch)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = listings.filter(item => {
    const matchSearch =
      item.commodity.toLowerCase().includes(search.toLowerCase()) ||
      item.seller.toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === 'Categories' || item.commodity === category;
    const matchGrade = grade === 'Grade' || item.grade === grade;
    return matchSearch && matchCategory && matchGrade;
  });

  return (
    <TraderLayout>
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <LuArrowLeft size={16} />
          Back
        </button>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <h1 className={styles.pageTitle}>Marketplace</h1>
          <div className={styles.toolbarRight}>
            <div className={styles.searchWrap}>
              <LuSearch size={14} color="#9ca3af" />
              <input
                className={styles.searchInput}
                placeholder="Search commodities..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <CustomSelect
              value={category}
              onChange={setCategory}
              options={['Categories', 'Maize', 'Wheat', 'Rice', 'Soybean']}
            />
            <CustomSelect
              value={grade}
              onChange={setGrade}
              options={['Grade', 'Grade A', 'Grade B', 'Grade C']}
            />
          </div>
        </div>

        {/* Listings grid */}
        {loading ? (
          <p style={{ color: 'var(--secondary-text)', padding: '2rem' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: 'var(--secondary-text)', padding: '2rem' }}>No approved listings available.</p>
        ) : (
          <div className={styles.grid}>
            {filtered.map(item => (
              <div key={item._id} className={styles.tripCard} onClick={() => setSelectedItem(item)}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTop}>
                    <span className={styles.loadIdText}>{item.commodity} • {item.grade}</span>
                    <span className={styles.verifiedBadge}>
                      <CheckmarkBadgeIcon />
                      Verified
                    </span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Quantity</span>
                    <span className={styles.infoValue}>{item.quantity}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Location</span>
                    <span className={styles.infoValue}>{item.location}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Seller</span>
                    <span className={styles.infoValue}>{item.seller}</span>
                  </div>
                  <div className={styles.cardDivider} />
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Unit price</span>
                    <span className={styles.infoValue}>{item.price}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Total amount</span>
                    <span className={styles.oliveValue}>{item.totalAmount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <MarketplaceItemDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onPlaceOrder={item => { setSelectedItem(null); setOrderingItem(item); }}
      />

      <PlaceOrderDrawer
        item={orderingItem}
        onClose={() => setOrderingItem(null)}
        onSubmit={() => { setOrderingItem(null); setShowSuccess(true); }}
      />

      {showSuccess && <SuccessModal onClose={() => setShowSuccess(false)} />}
    </TraderLayout>
  );
}
